import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getFarm, updateFarm, type FarmDetail } from "@/api/farms";
import { isApiError } from "@/api/errors";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { FarmForm, type FarmFormValues } from "../components/FarmForm";

export function FarmEditPage(): JSX.Element {
  const { farmId = "" } = useParams<{ farmId: string }>();
  const { t } = useTranslation("farms");
  const navigate = useNavigate();
  const [farm, setFarm] = useState<FarmDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFarm(farmId).then(
      (f) => {
        if (!cancelled) setFarm(f);
      },
      (err) => {
        if (!cancelled) {
          setError(isApiError(err) ? (err.problem.detail ?? err.problem.title) : String(err));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  const handleSubmit = async (values: FarmFormValues): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await updateFarm(farmId, values);
      navigate(`/farms/${farmId}`);
    } catch (err) {
      setError(isApiError(err) ? (err.problem.detail ?? err.problem.title) : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!farm) {
    return <p role="status">{t("detail.loading")}</p>;
  }

  return (
    <Page>
      {/* Single-column forms cap their own measure: a 1152px-wide field row
          puts unreadable distance between a label and its input. The page
          frame stays wide so the header lines up with every other page. */}
      <div className="flex max-w-3xl flex-col gap-4">
        <PageHeader
          above={
            <Breadcrumb
              items={[
                { label: t("list.heading"), to: "/farms" },
                { label: farm.name, to: `/farms/${farmId}` },
                { label: t("detail.edit") },
              ]}
            />
          }
          title={t("detail.edit")}
          subtitle={farm.name}
        />
        <FarmForm
          initial={{
            code: farm.code,
            name: farm.name,
            name_ar: farm.name_ar,
            description: farm.description,
            description_ar: farm.description_ar,
            governorate: farm.governorate,
            district: farm.district,
            nearest_city: farm.nearest_city,
            address_line: farm.address_line,
            farm_type: farm.farm_type,
            ownership_type: farm.ownership_type,
            primary_water_source: farm.primary_water_source,
            established_date: farm.established_date,
            tags: farm.tags,
            boundary: farm.boundary,
          }}
          submitLabel={t("form.submitUpdate")}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/farms/${farmId}`)}
          busy={busy}
          error={error}
        />
      </div>
    </Page>
  );
}
